import {useEffect, useMemo, useState} from 'react';
import {Flex, Box, Image} from '@chakra-ui/react';
import {CardTopBackground} from './card-tops/card-top-background';
import {CardTopCentered} from './card-tops/card-top-centered';
import {GeneralOfferingFooter} from './card-footer/general-offering-footer';
import {CollectionFooter} from './card-footer/collection-footer';
import {CardImage, CardHeadingSizes} from './card-utils';
import {getCmsTheme} from '../../utils/utils';
import {getModSettingsByType} from '../../utils/post-purchase';
import {PublicCollectionFooter} from './card-footer/public-collection-footer';

export interface CollectionCardProps {
  assetId: string;
  cardType: string;
  theme: string;
  slug: string;
  status: string;
  cardImage: CardImage;
  cardSubtitle: string;
  cardDescription: string;
  cardTitle: string;
  cardTitleSize: CardHeadingSizes;
  momentOfDelightType: string;
  displaySide: number;
  isDisabled?: boolean;
  isPublic?: boolean;
  collectionValue: number;
  collectionCurrency: string;
  collectionCurrencyPostfix: string;
  totalPieces: number;
  usersSignature?: string | null;
  onCertificateAction: (id: string) => void;
  onSellPiecesAction: () => void;
}

export const CollectionCard: React.FC<CollectionCardProps> = ({
  assetId,
  cardType,
  theme,
  cardImage,
  cardTitle,
  cardTitleSize,
  cardSubtitle,
  slug,
  status,
  cardDescription,
  momentOfDelightType,
  isDisabled,
  collectionValue,
  collectionCurrency,
  collectionCurrencyPostfix,
  totalPieces,
  displaySide,
  usersSignature,
  isPublic,
  onCertificateAction,
  onSellPiecesAction,
}) => {
  const themeColors = getCmsTheme(theme);
  const [modImage, setModImage] = useState<CardImage | undefined>(undefined);
  const [useSignature, setUseSignature] = useState(false);

  // This is a temp solution.
  // Will need to revisit at phase 4 Moments of delight select.
  const mod = useMemo(() => {
    return getModSettingsByType(momentOfDelightType);
  }, [momentOfDelightType]);

  useEffect(() => {
    switch (mod?.type) {
      case 'select-image':
      case 'select-portrait':
      case 'select-cover':
        if (displaySide && mod.images) {
          mod.images.forEach(({id, imageSrc}) => {
            if (id === displaySide) {
              setModImage({
                url: imageSrc,
              });
            }
          });
        } else {
          setModImage(undefined);
        }
        break;
      case 'sign-stamp':
        if (usersSignature) setUseSignature(true);
        break;
      case 'flip-coin':
        if (displaySide && mod.images) {
          mod.images.forEach(({id, imageSrc}) => {
            if (id === displaySide) {
              setModImage({
                url: imageSrc,
              });
            }
          });
        } else {
          setModImage(undefined);
        }
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mod, momentOfDelightType]);

  return cardTitle && cardType && cardImage.url ? (
    <Flex
      bgColor="superlight"
      boxShadow="0px 12px 34px rgba(0, 0, 0, 0.1)"
      borderRadius="24px"
      overflow="hidden"
      height="100%"
      direction="column"
      align="stretch"
      minH="100%"
      position="relative"
    >
      {cardType === 'centered' && (
        <CardTopCentered
          acentColour={themeColors?.secondary || ''}
          background={themeColors?.primary || ''}
          image={modImage || cardImage}
          title={cardTitle}
          titleSize={cardTitleSize}
          description={cardDescription}
          isDisabled={isDisabled}
          customImage={
            useSignature ? (
              <Box position="relative" width={170} height={170}>
                <Image
                  src="/assets/1c-hero-back.png"
                  alt="magenta 1c"
                  width="inherit"
                  height="inherit"
                />
                <Image
                  src={usersSignature || ''}
                  alt="signature"
                  width="40%"
                  pos="absolute"
                  top="60px"
                  right="5px"
                  transform="rotate(-30deg)"
                />
              </Box>
            ) : undefined
          }
        />
      )}
      {cardType === 'bg_image' && (
        <CardTopBackground
          acentColour={themeColors?.secondary || ''}
          background={themeColors?.primary || ''}
          image={modImage || cardImage}
          title={cardTitle}
          titleSize={cardTitleSize}
          subTitle={cardSubtitle}
          isDisabled={isDisabled}
        />
      )}

      {isDisabled && !isPublic && (
        <Box bg="white">
          <GeneralOfferingFooter
            btnTitle="Buy pieces"
            url={`/own-a-piece/${slug}`}
          />
        </Box>
      )}

      {!isDisabled && !isPublic && (
        <Box minH="135px">
          <CollectionFooter
            collectionValue={collectionValue}
            collectionCurrency={collectionCurrency}
            collectionCurrencyPostfix={collectionCurrencyPostfix}
            totalPieces={totalPieces}
            assetId={assetId}
            onCertificateAction={(id) => onCertificateAction(id)}
          />
        </Box>
      )}

      {isPublic && (
        <Box bg="white" minH="135px">
          <PublicCollectionFooter
            totalPieces={totalPieces}
            url={`/own-a-piece/${slug}`}
            btnTitle="Buy pieces"
          />
        </Box>
      )}
    </Flex>
  ) : (
    <></>
  );
};
